import { HttpResponse } from '../lib/http/http-response/http-response';
import { BusinessUnit } from '../lib/admin/model/business-unit';
import { Permissions } from '../lib/admin/model/permissions';
import { AgentSettings } from '../lib/admin/model/agent-settings';
import { CentralBrandingProfile } from '../lib/admin/model/central-branding-profile';
import { BrandingProfile } from '../lib/admin/model/branding-profile';

/**
 * This class to parse api response
 */
export class ApiParser {

    /**
     * Method to parse agent permissions response
     * 
     * @param permissions - permissions response
     * @returns - parsed permissions
     * @example - 
     * ```
     * parsePermissions(permissions)
     * ```
     */
    public parsePermissions(response: HttpResponse): Permissions[] {
        const data = response.data.permissions;
        const keys = [
          'agentsfsoftphone',
          'recordcontact',
          'masking',
          'muteagent',
          'multipartyconferencing',
          'hideagentidentityinfo',
          'hideagentcallerid',
          'hideagentphonenumber',
        ];
        const permission: Permissions[] = [];
        for (let i = 0; i < keys.length; i++) {
            data.forEach((record: any) => {
              if (record.Key.toLowerCase() === keys[i]) {
                permission.push({key: record.Key, value: record.Value});
              }
            });
        }
        return permission;
    }

    /**
    * Method to parse Agent settings response
    * 
    * @param settings  - AgentSettings response
    * @returns - parsed Agent settings
    * @example - 
    * ```
    * parseAgentSettings(settings)
    * ```
    */
    public parseAgentSettings(settings: HttpResponse): AgentSettings {
       const data = settings.data;
       const agentSettings: AgentSettings = {
            webRTCType: data.webRTCType,
            webRTCWssUrls: data.webRTCWSSUrls,
            webRTCServerDomain: data.webRTCServerDomain,
            webRTCIceUrls: data.webRTCICEUrls,
            webRTCDnis: data.webRTCDNIS
       };

       return agentSettings;
    }

    /**
    * Method to parse Business Unit response
    * 
    * @param settings  - BusinessUnit response
    * @returns - parsed Business Unit
    * @example - 
    * ```
    * parseBusinessUnit(businessUnitResponse)
    * ```
    */
    public parseBusinessUnit(businessUnitResponse: HttpResponse): BusinessUnit {
        const data = businessUnitResponse.data;
        const businessUnit: BusinessUnit = {
            features: data.businessUnits[0].features,
            maxConference: data.businessUnits[0].maxConferenceParties,
            fileExtList: data.businessUnits[0].fileExtensions
        };
        return businessUnit;
    }

    /**
    * Method to parse Central Branding Profile response
    * 
    * @param settings  - CentralBrandingProfile response
    * @returns - parsed Central Branding Profile
    * @example - 
    * ```
    * parseCentralBrandingProfile(centralBrandingProfileResponse)
    * ```
    */
    public parseCentralBrandingProfile(centralBrandingProfileResponse: HttpResponse): CentralBrandingProfile {
      const data = centralBrandingProfileResponse.data;
      const centralBrandingProfile: CentralBrandingProfile = {
        profileId: data.profileId,
        profileName: data.profileName,
        brandName: data.brandName,
        adminEmail: data.adminEmail,
        active: data.active,
        subdomain: data.subdomain,
        stylePackName: data.stylePackName,
        clientStyleName: data.clientStyleName,
        description: data.description,
        coBrand: data.coBrand,
        centralLogo: data.centralLogo,
        centralFavicon: data.centralFavicon,
        agentLogo: data.agentLogo,
        chatLogo: data.chatLogo,
        sfdcAgentLogo: data.sfdcAgentLogo,
        maxAgentLogo: data.maxAgentLogo,
        maxGlanceLogo: data.maxGlanceLogo,
        supervisorLogo: data.supervisorLogo,
      };
      return centralBrandingProfile;
    }

    /**
    * Method to parse CXone Branding Profile response
    * 
    * @param settings  - CXoneBrandingProfile response
    * @returns - parsed CXone Branding Profile
    * @example - 
    * ```
    * parseBrandingProfile(brandingProfileResponse)
    * ```
    */
    public parseBrandingProfile(brandingProfileResponse: HttpResponse): BrandingProfile {
      const data = brandingProfileResponse.data.brandingProfile;
      const brandingProfile: BrandingProfile = {
        profileId: data.profileId,
        profileName: data.profileName,
        description: data.description,
        brandName: data.brandName,
        systemEmail: data.systemEmail,
        hostName: data.hostName,
        status: data.status,
        coBrand: data.coBrand,
        cxoneLoginBgColor: data.cxoneLoginBgColor,
        cxoneHeaderIconTextColor: data.cxoneHeaderIconTextColor,
        maxStylePackName: data.maxStylePackName,
        supervisorStylePackName: data.supervisorStylePackName,
        marketingEnabled: data.marketingEnabled,
        marketingUrl: data.marketingUrl,
        partnerDefaultBP: data.partnerDefaultBP,
        cxoneLoginHeaderLogoUrl: data.cxoneLoginHeaderLogoUrl,
        cxoneEmailFormLogoUrl: data.cxoneEmailFormLogoUrl,
        cxoneFaviconUrl: data.cxoneFaviconUrl,
        maxGlanceBarExpLogoUrl: data.maxGlanceBarExpLogoUrl,
        maxGlanceBarCollLogoUrl: data.maxGlanceBarCollLogoUrl,
        supervisorLogoUrl: data.supervisorLogoUrl,
        sfdcLogoUrl: data.sfdcLogoUrl
      };
      return brandingProfile;
    }
}
